<template>
  <div class="vue-plotly">
    <div
      ref="plotly"
      v-loading="innerLoading || loading"
      :class="['plotly-plot', { 'hide-axistext': hideAxisText }]"
    />
  </div>
</template>

<script>
import Plotly from 'plotly.js-dist'
import debounce from 'lodash/debounce'
import merge from 'lodash/merge'
import { addListener, removeListener } from 'resize-detector'
import colors from '@/utils/constants/colors'
// import { mapMutations } from "vuex";

const methods = [
  'newPlot',
  'react',
  'restyle',
  'relayout',
  'update',
  'addTraces',
  'deleteTraces',
  'moveTraces',
  'extendTraces',
  'prependTraces',
  'addFrames',
  'animate',
  'purge',
  'toImage',
  'downloadImage'
]

const preLayout = {
  font: {
    family: 'Arial',
    size: 14
  },
  colorway: colors,
  autosize: true,
  xaxis: {
    automargin: true
  },
  yaxis: {
    automargin: true
  }
}

const preConfig = {
  displaylogo: false,
  scrollZoom: false,
  showTips: false,
  //displayModeBar: true
}

// const toImageOpts = {
//   format: "png",
//   scale: 4,
// };

export default {
  props: {
    data: {
      type: Array,
      default() {
        return []
      }
    },
    layout: {
      type: Object
    },
    config: {
      type: Object
    },
    frames: {
      type: Array
    },
    useResize: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    watchDeep: {
      type: Boolean,
      default: false
    },
    manualUpdate: {
      type: Boolean,
      default: false
    },
    hideAxisText: {
      type: Boolean,
      deafult: false
    }
  },
  data() {
    return {
      innerLoading: false
    }
  },

  computed: {
    figure() {
      return {
        data: this.data,
        layout: merge({}, preLayout, this.layout),
        config: merge({}, preConfig, this.config),
        frames: this.frames
      }
    }
  },

  mounted() {
    this.innerLoading = true

    this.initialized = false
    this.plot = {}
    this.plotEl = this.$refs.plotly
    this.Plotly = Plotly

    methods.forEach((method) => {
      this.plot[method] = (...args) => {
        return this.Plotly[method](this.plotEl, ...args)
      }
    })

    this.unwatchFigure = this.$watch(
      'figure',
      () => {
        this.plotReact()
      },
      {
        deep: this.watchDeep,
        immediate: true
      }
    )

    this.$once('hook:beforeDestroy', () => {
      if (this.useResize) {
        this.removeResizeListener()
      }
      this.removeEventListeners()
      this.plot.purge()
    })

    this.innerLoading = false
  },

  methods: {
    // ...mapMutations("project", ["addImage"]),
    plotReact() {
      // console.log('date 4:', new Date().toISOString())
      this.plot
        .react(this.figure)
        .then(() => {
          if (!this.initialized) {
            if (this.manualUpdate) {
              this.unwatchFigure()
            }
            this.$emit('plot-ready', this.plot)
            this.addEventListeners()
            if (this.useResize) {
              this.addResizeListener()
            }
            this.initialized = true
          }
          // console.log('date 5:', new Date().toISOString())
        })
        .catch((e) => {
            console.error("plot react error: ", e); // eslint-disable-line
        })
    },
    getPlotlyEventName(eventName) {
      return 'plotly_' + eventName
    },
    addEventListeners() {
      Object.keys(this.$listeners).forEach((event) => {
        const eventName = this.getPlotlyEventName(event)
        const handler = this.$listeners[event]
        this.plotEl.on(eventName, handler)
      })
    },
    removeEventListeners() {
      Object.keys(this.$listeners).forEach((event) => {
        const eventName = this.getPlotlyEventName(event)
        this.plotEl.removeAllListeners(eventName)
      })
    },
    addResizeListener() {
      this.resizeHandler = debounce(
        (el) => {
          if (
            !el.offsetParent ||
              el.offsetWidth === 0 ||
              el.offsetHeight === 0
          ) {
            return
          }
          this.Plotly.Plots.resize(el)
        },
        100,
        { leading: true }
      )
      addListener(this.plotEl, this.resizeHandler)
    },
    removeResizeListener() {
      removeListener(this.plotEl)
      this.resizeHandler = null
    }
  }
}
</script>

  <style lang="scss" scoped>
  .vue-plotly {
    width: 100%;
    height: 100%;
    /*min-height: 400px; olicz note plotly css change*/
    position: relative;

    .plotly-plot {
      width: 100%;
      height: 100%;
    }
  }
  </style>

  <style lang="scss">
  /* Minimal Plotly mode bar - top left with clean white background (default) */
  .js-plotly-plot .plotly .modebar {
    position: absolute !important;
    top: 8px !important;
    left: 8px !important;
    right: auto !important;
    background: white !important;
    border: 1px solid #e0e0e0 !important;
    border-radius: 6px !important;
    padding: 4px !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08) !important;
  }

  /* Exception: Keep mode bar on the right for survival plots */
  .survival-plot-container .js-plotly-plot .plotly .modebar {
    left: auto !important;
    right: 8px !important;
  }

  /* Exception: Keep mode bar on the right for clinical correlation scatter plots */
  [data-module="clinicalCorrelation"] .plot-container .js-plotly-plot .plotly .modebar,
  .clinical-scatter-plot .js-plotly-plot .plotly .modebar {
    left: auto !important;
    right: 8px !important;
  }

  /* No padding for modebar group */
  .js-plotly-plot .plotly .modebar-group {
    padding: 0 !important;
    margin: 0 !important;
  }

  /* Minimal button styling */
  .js-plotly-plot .plotly .modebar-btn {
    border-radius: 4px !important;
    margin: 2px !important;
    padding: 4px !important;
    transition: all 0.15s ease !important;
  }

  .js-plotly-plot .plotly .modebar-btn:hover {
    background: #f5f5f5 !important;
  }

  /* Black icons for minimal look */
  .js-plotly-plot .plotly .modebar-btn path {
    fill: #000 !important;
  }

  .js-plotly-plot .plotly .modebar-btn:hover path {
    fill: #000 !important;
  }

  /* Slightly larger icons for better visibility */
  .js-plotly-plot .plotly .modebar-btn svg {
    width: 18px !important;
    height: 18px !important;
  }
  </style>

